// Versão: 1.02
// Data..: 2026-10-01
// Cliente HTTP para as rotas públicas da API (sem autenticação).
// v1.02: merge Frentes D+E — getPayment/PublicPaymentView + expiração do link de assinatura.
// v1.01: getPayment + PublicPaymentView (link público de pagamento por token);
//        PublicSignView.expired/expiresAt (expiração do link de assinatura).

import { config } from '../config.js';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(detail.message ?? `Erro ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const publicApi = {
  listArticles: () => request<{ items: PublicArticle[] }>('GET', '/public/kb'),
  listCategories: () => request<{ items: PublicCategory[] }>('GET', '/public/categories'),
  getArticle: (slug: string) => request<PublicArticle>('GET', `/public/kb/${slug}`),
  articleFeedback: (id: string, helpful: boolean) =>
    request<{ ok: boolean }>('POST', `/public/kb/${id}/feedback`, { helpful }),
  openTicket: (data: PublicTicketInput) =>
    request<{ received: boolean }>('POST', '/public/tickets', data),
  getSurvey: (token: string) => request<PublicSurveyView>('GET', `/public/survey/${token}`),
  answerSurvey: (token: string, data: { rating: number; comment?: string }) =>
    request<{ ok: boolean }>('POST', `/public/survey/${token}`, data),
  // Assinatura eletrônica (por token).
  getSignRequest: (token: string) => request<PublicSignView>('GET', `/public/sign/${token}`),
  submitSignature: (token: string, data: SignSubmitPayload) =>
    request<{ completed: boolean }>('POST', `/public/sign/${token}`, data),
  declineSignature: (token: string, reason?: string) =>
    request<{ declined: boolean }>('POST', `/public/sign/${token}/decline`, { reason }),
  // Link público de pagamento (por token).
  getPayment: (token: string) => request<PublicPaymentView>('GET', `/public/pay/${token}`),
};

// Visão pública mínima da cobrança (espelho do DTO em @makweb/shared).
export interface PublicPaymentView {
  number: number;
  clientName: string;
  description: string;
  amount: number;
  dueDate: string;
  status: string;
  paid: boolean;
  pixCopiaECola?: string;
  linhaDigitavel?: string;
  codigoBarras?: string;
  boletoPdfAvailable: boolean;
}

export interface PublicSignView {
  title: string;
  quoteNumber?: number;
  signerName: string;
  signerRole?: string;
  alreadySigned: boolean;
  alreadyDeclined: boolean;
  status: string;
  documentUrl?: string;
  signedCount: number;
  totalCount: number;
  expired: boolean;
  expiresAt?: string;
}

export interface SignSubmitPayload {
  fullName: string;
  signatureImage?: string;
  signatureType?: 'drawn' | 'typed';
  accepted: boolean;
}

export interface PublicSurveyView {
  ticketNumber?: number;
  subject?: string;
  answered: boolean;
  rating?: number;
  comment?: string;
}

export interface PublicArticle {
  id: string;
  title: string;
  slug: string;
  body: string;
  updatedAt: string;
  views?: number;
  categoryId?: string;
  tags?: string[];
}

export interface PublicCategory {
  id: string;
  name: string;
}

export interface PublicTicketInput {
  name: string;
  email: string;
  subject: string;
  message: string;
  department?: string;
  // Anti-robô: campo honeypot (deve vir vazio) e carimbo de quando o form abriu.
  company_website?: string; // honeypot (oculto)
  formOpenedAt?: number; // epoch ms
}
